// Loads an image for canvas drawing (share posters) without tainting the canvas.
// Media already shown in an <img> is cached by the browser WITHOUT CORS headers
// (R2 only adds Access-Control-Allow-Origin when the request carries an Origin),
// and Chrome/WebKit reuse that entry for a crossOrigin="anonymous" <img> → blocked.
// So cross-origin media is re-fetched with cache:"reload" and drawn from a blob URL.
const withTimeout = (promise, ms, fallback) => new Promise((res) => {
  const tm = setTimeout(() => res(fallback), ms);
  promise.then((v) => { clearTimeout(tm); res(v); }, () => { clearTimeout(tm); res(fallback); });
});

const loadTag = (src, cors) => new Promise((res) => {
  const im = new Image();
  if (cors) im.crossOrigin = "anonymous";
  im.onload = () => res(im);
  im.onerror = () => res(null);
  im.src = src;
});

const fetchAsImage = async (src, ms) => {
  const ctl = new AbortController();
  const tm = setTimeout(() => ctl.abort(), ms);
  try {
    const r = await fetch(src, { mode: "cors", credentials: "omit", cache: "reload", signal: ctl.signal });
    if (!r.ok) return null;
    const url = URL.createObjectURL(await r.blob());
    const im = await loadTag(url, false);
    URL.revokeObjectURL(url);
    return im;
  } catch {
    return null;
  } finally {
    clearTimeout(tm);
  }
};

export const loadCanvasImage = async (src, cors = true, timeoutMs = 8000) => {
  if (!src) return null;
  const local = /^(data:|blob:)/.test(src) || (!cors && !/^https?:\/\//.test(src));
  if (local || !cors) return withTimeout(loadTag(src, false), timeoutMs, null);
  return (await fetchAsImage(src, timeoutMs)) || withTimeout(loadTag(src, true), timeoutMs, null);
};

export default loadCanvasImage;
